// LLM generated - Model Claude 4 Sonnet
import { useEffect } from 'react';
import { Skeleton, Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { match } from 'ts-pattern';
import Link from 'next/link';

import { Title } from '~/domains/design-system/Title';
import type { PaymentType } from '~/common/enums/payments.enum';
import { ErrorAlert } from '~/domains/common/components/Alerts/Alerts';
import { EmptyState } from '~/domains/design-system/components/EmptyState';
import { Text } from '~/domains/design-system/Text';
import { IconAlertCircle } from '@tabler/icons-react';
import { SUPPORT_EMAIL } from '~/common/constants/support.const';
import type { IGetWalletsRefundableBreakdown } from '~/services/wallet/interfaces/wallets.interface';

import { useWithdrawalFormContext } from '../context/withdrawal.context';
import { RefundMethodOption } from './RefundMethodOption/RefundMethodOption';
import { RefundMethodOptionSkeleton } from './RefundMethodOption/RefundMethodOptionSkeleton';

type RefundPaymentMethodsProps = {
  refundableBreakdown: IGetWalletsRefundableBreakdown['data'] | undefined;
  isFetchingRefundableBreakdown: boolean;
  hasConsistencyError: boolean;
};

export function RefundPaymentMethods({
  refundableBreakdown,
  isFetchingRefundableBreakdown,
  hasConsistencyError,
}: RefundPaymentMethodsProps) {
  const { t } = useTranslation('payments');
  const form = useWithdrawalFormContext();
  const { paymentType } = form.values;

  // Auto-select first available refund option when data loads
  useEffect(() => {
    if (
      !isFetchingRefundableBreakdown &&
      !hasConsistencyError &&
      refundableBreakdown?.items &&
      refundableBreakdown.items.length > 0 &&
      !paymentType // Only auto-select if no payment type is already selected
    ) {
      const firstItem = refundableBreakdown.items[0];
      form.setFieldValue('paymentType', firstItem.paymentMethod as PaymentType);
      form.setFieldValue('withdrawalAmount', firstItem.refundableBalance);
    }
  }, [isFetchingRefundableBreakdown, hasConsistencyError, refundableBreakdown, paymentType, form]);

  // Show consistency error state - balance data doesn't match
  if (hasConsistencyError) {
    return (
      <EmptyState showBorder>
        <EmptyState.Icon>
          <IconAlertCircle size={48} />
        </EmptyState.Icon>
        <EmptyState.Title>{t('refundPaymentMethods.consistencyError.title')}</EmptyState.Title>
        <EmptyState.Text>
          <Trans
            i18nKey="payments:refundPaymentMethods.consistencyError.description"
            components={{
              link: (
                <Text
                  variant="body-large"
                  component={Link}
                  href={`mailto:${SUPPORT_EMAIL.DEFAULT}?subject=Refund Balance Data Issue`}
                  c="blue.7"
                  fw={700}
                />
              ),
            }}
          />
        </EmptyState.Text>
      </EmptyState>
    );
  }

  // Show error only if not loading and no data
  if (
    !isFetchingRefundableBreakdown &&
    (!refundableBreakdown?.items || refundableBreakdown.items.length === 0)
  ) {
    return (
      <ErrorAlert px="lg" mt="xs">
        {t('refund.noRefundsAvailable')}
      </ErrorAlert>
    );
  }

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
        {isFetchingRefundableBreakdown ? (
          <>
            <RefundMethodOptionSkeleton />
            <RefundMethodOptionSkeleton />
          </>
        ) : (
          refundableBreakdown?.items?.map((item) => {
            const displayName = match(item.paymentMethod)
              .with('PAYPAL', () => t('paymentMethods.paypal'))
              .with('VENMO', () => t('paymentMethods.venmo'))
              .with('SKRILL', () => t('paymentMethods.skrill'))
              .with('AEROPAY', () => t('paymentMethods.bankAccount'))
              .with('CARD', () => t('paymentMethods.debitCard'))
              .otherwise(() => item.paymentMethod);

            return (
              <RefundMethodOption
                key={item.paymentMethod}
                value={item.paymentMethod}
                label={displayName}
                amount={item.refundableBalance}
                isSelected={paymentType === item.paymentMethod}
                paymentMethod={item.paymentMethod}
                onChange={() => {
                  form.setFieldValue('paymentType', item.paymentMethod as PaymentType);
                  form.setFieldValue('withdrawalAmount', item.refundableBalance);
                }}
              />
            );
          })
        )}
      </Stack>
    </Stack>
  );
}
